import CommonTitleBar from "components/common/CommonTitleBar";
import DeleteConfirmationModal from "components/common/ConfirmationDialog";
import CustomField from "components/common/CustomField";
import CustomLabel from "components/common/CustomLabel";
import IsLoading from "components/common/IsLoading";
import NoDataFound from "components/common/NoDataFound";
import Paginations from "components/common/Paginations";
import Spinners from "components/common/Spinners";
import { formatDate, handleStopPropagation } from "components/common/Utils";
import { addRoleSchema } from "components/common/Validation";
import { addRoleValues } from "components/constants/InitialFormValues";
import { permissions } from "components/constants/Permissions";
import TableShimmer from "components/ShimmerEffects/CommonShimmer/TableShimmer";
import { Form, Formik } from "formik";
import { Ellipsis, Search, X } from "lucide-react";
import { Checkbox } from "primereact/checkbox";
import { InputSwitch } from "primereact/inputswitch";
import React, { useEffect, useState } from "react";
import {
  Badge,
  Col,
  Container,
  Dropdown,
  Modal,
  Row,
  Table,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  addRole,
  deleteRole,
  getAllRoles,
  getRoleDetails,
  updateRole,
  updateRoleStatus,
} from "store/actions/rolesSlice";
import { showAlert } from "store/reducers/alertReducer";

const Roles = () => {
  const { t } = useTranslation();
  document.title = t("Roles | Work Space");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { search } = useLocation();
  const pageNumber = Number(new URLSearchParams(search).get("page")) || 1;

  const {
    isLoading,
    loading,
    perPage,
    totalCount,
    totalPages,
    currentPage,
    roles,
    roleDetails,
    gettingDetails,
  } = useSelector((state) => state.Roles);

  const [selectedId, setSelectedId] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [showModal, setShowModal] = useState(false); // DELETE MODAL
  const [modalType, setModalType] = useState(null);
  const [show, setShow] = useState(false); // ADD/EDIT MODAL
  const [showDropdown, setShowDropdown] = useState(false);

  const [formValues, setFormValues] = useState({
    name: "",
    nameAr: "",
    permissions: permissions,
  });

  useEffect(() => {
    getAllData();
  }, []);

  const getAllData = (page, searchKeyword = keyword) => {
    const payload = {
      perPage,
      pageNo: page || pageNumber,
      status: -1,
      orderBy: -1,
      dateRange: {},
      keyword: searchKeyword || "",
    };
    dispatch(getAllRoles(payload));
    navigate(`/roles?page=${page || pageNumber}`);
  };

  const handleShow = () => {
    setShow(true);
  };
  const handleClose = () => {
    setShow(false);
    setModalType(null);
    setFormValues(addRoleValues);
  };

  useEffect(() => {
    const getDetails = () => {
      const payload = {
        id: selectedId,
      };
      dispatch(getRoleDetails(payload));
    };
    if (["EDIT", "VIEW"]?.includes(modalType)) {
      getDetails();
    }
  }, [modalType]);

  // SET FORM FIELD
  useEffect(() => {
    if (
      roleDetails &&
      !gettingDetails &&
      ["EDIT", "VIEW"]?.includes(modalType)
    ) {
      const { name, nameAr, permissions } = roleDetails || {};
      setFormValues({ name, nameAr, permissions });
    }
  }, [roleDetails, gettingDetails]);

  const handleStatusChange = (id, status) => {
    setSelectedId(id);
    const payload = {
      id,
      status: status === 1 ? 0 : 1,
    };

    dispatch(updateRoleStatus(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          setSelectedId(null);
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
          setSelectedId(null);
        }
      })
      .catch((err) => {});
  };

  const handleDelete = () => {
    const payload = {
      id: selectedId,
      status: "2", // 2 FOR DELETE
    };
    dispatch(deleteRole(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          setShowModal(false);
          setSelectedId(null);
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  const handleSubmit = (values, { resetForm }) => {
    const { name, nameAr, permissions } = values;
    if (
      !permissions?.some((group) =>
        group?.children?.some((perm) => perm.permissions.isView)
      )
    ) {
      dispatch(
        showAlert({
          text: "At least one permission must be selected!",
          type: "error",
        })
      );
      return;
    }

    const payload = {
      name,
      nameAr,
      permissions,
    };

    if (modalType === "ADD") {
      dispatch(addRole(payload))
        .then((action) => {
          const { statusCode, success, message } = action?.payload;
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            handleClose();
            resetForm();
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
          }
        })
        .catch((err) => {});
    } else if (modalType === "EDIT") {
      const updatedPayload = {
        ...payload,
        id: selectedId,
      };
      dispatch(updateRole(updatedPayload))
        .then((action) => {
          const { statusCode, success, message } = action?.payload;
          if (statusCode === 200 && success) {
            dispatch(showAlert({ text: message, type: "success" }));
            handleClose();
            resetForm();
          } else {
            dispatch(showAlert({ text: message, type: "error" }));
          }
        })
        .catch((err) => {});
    }
  };

  const toggleAllPermissions = (values, setFieldValue, isChecked) => {
    const updatedPermissions = values?.permissions?.map((group) => ({
      ...group,
      children: group.children.map((perm) => ({
        ...perm,
        permissions: { ...perm.permissions, isView: isChecked }, // Fix here
      })),
    }));

    setFieldValue("permissions", updatedPermissions);
  };

  // const toggleGroupPermissions = (
  //   values,
  //   setFieldValue,
  //   groupIdx,
  //   isChecked
  // ) => {
  //   const updatedPermissions = values.permissions.map((group, idx) =>
  //     idx === groupIdx
  //       ? {
  //           ...group,
  //           children: group.children.map((perm) => ({
  //             ...perm,
  //             permissions: { ...perm.permissions, isView: isChecked }, // Fix here
  //           })),
  //         }
  //       : group
  //   );

  //   setFieldValue("permissions", updatedPermissions);
  // };

  const isAllSelected = (values) =>
    values?.permissions?.every(
      (group) => group?.children?.every((perm) => perm.permissions.isView) // Fix here
    );

  // const isGroupSelected = (group) =>
  //   group.children.every((perm) => perm.isView);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      getAllData();
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setKeyword(value);

    // Call handleSearch when input becomes empty
    if (value.trim() === "") {
      setTimeout(() => {
        getAllData(pageNumber, "");
      }, 100);
    }
  };

  const handleReset = () => {
    setKeyword("");
    getAllData(pageNumber, "", true);
  };

  return (
    <React.Fragment>
      <div className="page-content common_page">
        <Container fluid>
          <CommonTitleBar
            title={t("Roles")}
            isCreateBtn={true}
            createBtnText={t("Add New")}
            onCreateBtnClick={() => {
              setModalType("ADD");
              handleShow();
            }}
          />
          <div className="common_wrapper">
            <Row>
              <Col>
                <div className="common_filter">
                  <div className="d-flex align-items-center gap-3">
                    <div className="search_input">
                      <input
                        type="text"
                        placeholder={t("Search")}
                        value={keyword}
                        onChange={(e) => handleSearchChange(e)}
                        onKeyDown={handleKeyDown}
                      />
                      <Search width={16} height={16} className="search_icon" />
                    </div>
                    <div className="buttons">
                      <button
                        className="search_btn"
                        onClick={() => getAllData()}
                      >
                        {t("Search")}
                      </button>
                      <button className="reset_btn" onClick={handleReset}>
                        {t("Reset")}
                      </button>
                    </div>
                  </div>
                </div>
              </Col>
              <Col lg={12}>
                {loading ? (
                  <TableShimmer col={6} />
                ) : roles.length === 0 ? (
                  <NoDataFound />
                ) : (
                  <div
                    className="common_table_style"
                    style={{ minHeight: showDropdown ? 250 : "unset" }}
                  >
                    <Table>
                      <thead className="t_head">
                        <tr>
                          <th>#</th>
                          <th>{t("Name(English)")}</th>
                          <th>{t("Name(Arabic)")}</th>
                          <th>{t("Created At")}</th>
                          <th>{t("Status")}</th>
                          <th>{t("Actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {roles?.map((item, index) => (
                          <tr key={item?._id}>
                            <td>{(currentPage - 1) * perPage + index + 1}</td>
                            <td>{item.name || "-"}</td>
                            <td>{item?.nameAr || "-"}</td>
                            <td>{formatDate(item?.createdAt)}</td>
                            <td>
                              {" "}
                              <Badge
                                bg={
                                  item.status === 1
                                    ? " bg_primary"
                                    : " bg-secondary"
                                }
                              >
                                {item.status === 1
                                  ? t("Active")
                                  : t("Deactive")}
                              </Badge>
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-3">
                                <InputSwitch
                                  checked={item.status === 1}
                                  disabled={
                                    isLoading && selectedId === item._id
                                  }
                                  onChange={(e) =>
                                    handleStatusChange(item._id, item.status)
                                  }
                                />
                                <Dropdown
                                  className="custom_dropdown list_action_dropdown"
                                  drop={"down"}
                                  onClick={handleStopPropagation}
                                  autoClose="outside"
                                  onToggle={(isOpen) => setShowDropdown(isOpen)}
                                >
                                  <Dropdown.Toggle>
                                    <Ellipsis className="text_black" />
                                  </Dropdown.Toggle>

                                  <Dropdown.Menu align="end">
                                    <Dropdown.Item
                                      href="#"
                                      onClick={() => {
                                        setSelectedId(item._id);
                                        setModalType("VIEW");
                                        handleShow();
                                      }}
                                    >
                                      {t("View")}
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      href="#"
                                      onClick={() => {
                                        setSelectedId(item._id);
                                        setModalType("EDIT");
                                        handleShow();
                                      }}
                                    >
                                      {t("Edit")}
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      href="#"
                                      className="text_danger"
                                      onClick={() => {
                                        setSelectedId(item._id);
                                        setShowModal(true);
                                      }}
                                    >
                                      {t("Delete")}
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Col>
            </Row>
          </div>
          {!loading && (
            <Paginations
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => {
                getAllData(page);
              }}
              totalCount={totalCount}
            />
          )}
        </Container>
      </div>
      <DeleteConfirmationModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedId(null);
        }}
        onConfirm={handleDelete}
        message={t("Are you sure you want to delete this {{name}}?", {
          name: "User",
        })}
        isLoading={isLoading}
      />

      <Modal
        centered
        size="lg"
        show={show}
        onHide={handleClose}
        className="common_modal"
      >
        <div className="modal_header g_between">
          <h2 className="fs_17">
            {modalType === "ADD"
              ? t("Add Role")
              : modalType === "VIEW"
              ? t("Role Details")
              : t("Edit Role")}
          </h2>
          <X
            onClick={handleClose}
            className="text_black c_p"
            width={20}
            height={20}
          />
        </div>
        <div className="modal_body mt-0">
          {["EDIT", "VIEW"]?.includes(modalType) && gettingDetails ? (
            <IsLoading />
          ) : (
            <div>
              <Formik
                initialValues={formValues}
                validationSchema={addRoleSchema}
                enableReinitialize={true}
                onSubmit={handleSubmit}
              >
                {({ setFieldValue, values, errors, touched }) => (
                  <Form>
                    <Row>
                      <Col lg={6}>
                        <div className="form_field mt_13">
                          <CustomLabel
                            htmlFor="name"
                            label={t("Name(English)")}
                          />
                          <CustomField
                            name="name"
                            type="text"
                            placeholder={t("Name(English)")}
                            touched={touched}
                            errors={errors}
                            isReadOnly={modalType === "VIEW"}
                          />
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="form_field mt_13">
                          <CustomLabel
                            htmlFor="nameAr"
                            label={t("Name(Arabic)")}
                          />
                          <CustomField
                            name="nameAr"
                            type="text"
                            placeholder={t("Name(Arabic)")}
                            touched={touched}
                            errors={errors}
                            isArabic={true}
                            isReadOnly={modalType === "VIEW"}
                          />
                        </div>
                      </Col>

                      <Col lg={12}>
                        {/* Select All Checkbox */}
                        <div className="d-flex gap-2 align-items-center my-3">
                          <Checkbox
                            inputId="selectAll"
                            checked={isAllSelected(values)}
                            onChange={(e) =>
                              toggleAllPermissions(
                                values,
                                setFieldValue,
                                e.checked
                              )
                            }
                            disabled={modalType === "VIEW"}
                          />
                          <label
                            htmlFor="selectAll"
                            className="fs_16 text_black"
                          >
                            {t("Select All Permissions")}
                          </label>
                        </div>
                      </Col>

                      {/* Permission Checkboxes */}
                      {/* {console.log(values?.permissions[0])} */}
                      {values?.permissions?.map((group, groupIdx) => (
                        <div key={group.id} className="mb-3">
                          {/* Group Toggle Checkbox */}
                          <div className="d-flex gap-2 align-items-center">
                            {/* <Checkbox
                              inputId={`group_${group.id}`}
                              checked={isGroupSelected(group)}
                              onChange={(e) =>
                                toggleGroupPermissions(
                                  values,
                                  setFieldValue,
                                  groupIdx,
                                  e.checked
                                )
                              }
                            /> */}
                            <h5 className="fs_18 text_primary">{group.name}</h5>
                          </div>
                          <Row>
                            {group.children.map((perm, permIdx) => (
                              <Col lg={4} key={perm.id}>
                                <div className="d-flex gap-2 align-items-center my-2 pb-2">
                                  <Checkbox
                                    inputId={perm.id}
                                    checked={perm.permissions.isView} // Ensure correct access
                                    onChange={(e) => {
                                      setFieldValue(
                                        "permissions",
                                        values.permissions.map((g, gIdx) =>
                                          gIdx === groupIdx
                                            ? {
                                                ...g,
                                                children: g.children.map(
                                                  (p, pIdx) =>
                                                    pIdx === permIdx
                                                      ? {
                                                          ...p,
                                                          permissions: {
                                                            ...p.permissions,
                                                            isView: e.checked,
                                                          }, // Fix here
                                                        }
                                                      : p
                                                ),
                                              }
                                            : g
                                        )
                                      );
                                    }}
                                    disabled={modalType === "VIEW"}
                                  />
                                  <label
                                    htmlFor={perm.id}
                                    className="fs_16 text_black"
                                  >
                                    {perm.name}
                                  </label>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        </div>
                      ))}
                      {modalType !== "VIEW" && (
                        <Col lg={12}>
                          <div className="g_between mt-4 gap-3">
                            <button
                              type="button"
                              className="cancel_btn w-50"
                              onClick={handleClose}
                            >
                              {t("Cancel")}
                            </button>
                            <button
                              type="submit"
                              className="main_btn w-50"
                              disabled={isLoading}
                            >
                              {modalType === "ADD" ? t("Add") : t("Save")}
                            </button>
                          </div>
                        </Col>
                      )}
                    </Row>
                  </Form>
                )}
              </Formik>
            </div>
          )}
        </div>
      </Modal>
    </React.Fragment>
  );
};

export default Roles;
